import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Card from 'reactstrap/lib/Card';

import {
  BuildPriceCreativeConfigDefaultProps,
  BuildPriceCreativeConfigPropTypes,
} from 'client/data/models/native-ads-creative-config';
import {
  CommonAdDefaultProps,
  CommonAdPropTypes,
  SiteServedAdDefaultProps,
  SiteServedAdPropTypes,
} from 'site-modules/shared/components/ad-unit/ad-unit-prop-types';

import { BUILD_PRICE_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import { definePlatform } from 'site-modules/shared/components/native-ad/utils/utils';
import { siteServedAdTrackingWrapper } from 'site-modules/shared/components/native-ad/site-served-ad-tracking-wrapper/site-served-ad-tracking-wrapper';
import { AdLabel } from 'client/site-modules/shared/components/native-ad/native-ad-components/ad-label/ad-label';
import { Disclaimer } from 'client/site-modules/shared/components/native-ad/native-ad-components/disclaimer/disclaimer';
import { ConquestRibbon } from 'site-modules/shared/components/native-ad/native-ad-components/conquest-ribbon/conquest-ribbon';
import { DfpTracking } from 'client/site-modules/shared/components/native-ad/utils/dfp-tracking';
import { CPO_PRODUCT_SKU } from 'site-modules/shared/components/native-ad/native-ad-components/cpo-features/cpo-constants';

import { BuildPriceAtAGlance } from './buildprice-at-a-glance';
import { BuildPriceFeatures } from './buildprice-features';

import './buildprice-site-served-ad.scss';

export function BuildPriceSiteServedAdUI({
  adRef,
  adClickTrackingListener,
  adLoadTrackingListener,
  adTrackers,
  customTargeting = CommonAdDefaultProps.customTargeting,
  vehicle = SiteServedAdDefaultProps.vehicle,
  currentVehicle = SiteServedAdDefaultProps.currentVehicle,
  creativeConfig = BuildPriceCreativeConfigDefaultProps,
  isConquest = SiteServedAdDefaultProps.isConquest,
  mobile = SiteServedAdDefaultProps.mobile,
  position = SiteServedAdDefaultProps.position,
  wrapperClass = null,
  showOnlyNarrowView = false,
  hideJelly = false,
}) {
  const {
    siteServedAdsUpdater,
    creativeConfigData,
    traffickingData: { clickTracker },
    shuffleFeatures,
  } = creativeConfig;

  const { headline, cta, disclaimerCopy, lineItemId, siteServedCreativeId, target, vehicleInfo, features, productSku } =
    creativeConfigData;
  const loadTrackingListener = useCallback(
    event => {
      adLoadTrackingListener(event, cta);
    },
    [adLoadTrackingListener, cta]
  );

  if (!headline || !cta) {
    return null;
  }

  const targetVehicle = currentVehicle || vehicle;
  const ctaURL = clickTracker[definePlatform(mobile)];
  const ctaTarget = target || '_blank';
  const modelYear = get(vehicleInfo, 'year', '');

  const nativeStyle = BUILD_PRICE_AD.NATIVE_STYLE;

  const isFeaturesBuildPrice = get(features, 'length');

  const isCpoFeaturesEnabled = productSku?.includes(CPO_PRODUCT_SKU);

  return (
    <Card
      className={classnames('buildprice-site-served pos-r', wrapperClass, {
        'w-100': !mobile,
        'show-dropshadow': !isFeaturesBuildPrice || isCpoFeaturesEnabled,
      })}
      data-tracking-parent={nativeStyle}
      data-viewability={`${nativeStyle}-${position}`}
      data-make-name={get(targetVehicle, 'make.name', '')}
      data-model-name={get(targetVehicle, 'model.name', '')}
      data-model-year={modelYear}
      innerRef={adRef}
      style={{ overflow: 'hidden' }}
    >
      <DfpTracking
        lineItemId={lineItemId}
        siteServedCreativeId={siteServedCreativeId}
        mobile={mobile}
        nativeStyle={nativeStyle}
        position={position}
        currentVehicle={currentVehicle}
        customTargeting={customTargeting}
        slotRenderEndListener={loadTrackingListener}
        siteServedAdsUpdater={siteServedAdsUpdater}
      />
      {adTrackers}
      <div
        className={classnames('px-1_25 py-1_5', {
          'bg-gradient-ads': !isFeaturesBuildPrice || isCpoFeaturesEnabled,
        })}
      >
        {isConquest && (
          <ConquestRibbon style={{ height: '20px', lineHeight: '20px', top: '8px', left: '0px' }} classes="pos-a" />
        )}
        <AdLabel />
        {!isCpoFeaturesEnabled && isFeaturesBuildPrice ? (
          <BuildPriceFeatures
            creativeConfigData={creativeConfigData}
            ctaURL={ctaURL}
            ctaTarget={ctaTarget}
            adClickTrackingListener={adClickTrackingListener}
            features={shuffleFeatures}
            mobile={mobile}
            isConquest={isConquest}
            showOnlyNarrowView={showOnlyNarrowView}
            hideJelly={hideJelly}
          />
        ) : (
          <BuildPriceAtAGlance
            creativeConfigData={creativeConfigData}
            ctaURL={ctaURL}
            ctaTarget={ctaTarget}
            adClickTrackingListener={adClickTrackingListener}
            mobile={mobile}
            isConquest={isConquest}
            showOnlyNarrowView={showOnlyNarrowView}
            isCpoFeaturesEnabled={isCpoFeaturesEnabled}
            hideJelly={hideJelly}
          />
        )}
      </div>
      {disclaimerCopy && (
        <Disclaimer
          disclaimerText={disclaimerCopy}
          buttonClasses="pos-a"
          buttonStyle={{ right: '4px', bottom: '4px' }}
        />
      )}
    </Card>
  );
}

BuildPriceSiteServedAdUI.propTypes = {
  ...SiteServedAdPropTypes,
  customTargeting: CommonAdPropTypes.customTargeting,
  creativeConfig: BuildPriceCreativeConfigPropTypes,
  wrapperClass: PropTypes.string,
  showOnlyNarrowView: PropTypes.bool,
  hideJelly: PropTypes.bool,
};

export const BuildPriceSiteServedAd = siteServedAdTrackingWrapper(BuildPriceSiteServedAdUI);
